import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InspectionSection = ({ section, sectionNumber, data, onComplete, isExpanded, onExpand }) => {
  const [photos, setPhotos] = useState(data?.photos || []);
  const [notes, setNotes] = useState(data?.notes || '');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    processFiles(files);
  };

  const processFiles = (files) => {
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    const newPhotos = imageFiles?.map((file) => ({
      id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      name: file?.name,
      timestamp: new Date()?.toISOString()
    }));
    const updatedPhotos = [...photos, ...newPhotos];
    setPhotos(updatedPhotos);
    onComplete(section?.id, updatedPhotos, notes);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    processFiles(files);
  };

  const handleRemove = (photoId) => {
    const updatedPhotos = photos?.filter(photo => photo?.id !== photoId);
    setPhotos(updatedPhotos);
    onComplete(section?.id, updatedPhotos, notes);
  };

  const handleNotesChange = (e) => {
    const newNotes = e?.target?.value;
    setNotes(newNotes);
    onComplete(section?.id, photos, newNotes);
  };

  const isComplete = photos?.length >= section?.requiredPhotos;
  const remainingPhotos = Math.max(0, section?.requiredPhotos - photos?.length);

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Section Header */}
      <button
        onClick={onExpand}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-smooth"
      >
        <div className="flex items-center space-x-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isComplete ? 'bg-success text-success-foreground' : 'bg-primary/10 text-primary'
          }`}>
            {isComplete ? (
              <Icon name="CheckCircle2" size={20} strokeWidth={2.5} />
            ) : (
              <span className="font-heading font-bold text-sm">{sectionNumber}</span>
            )}
          </div>
          <div className="text-left">
            <div className="flex items-center space-x-2">
              <Icon name={section?.icon} size={18} strokeWidth={2} />
              <h3 className="font-heading text-lg font-semibold text-foreground">
                {section?.title}
              </h3>
            </div>
            <p className="font-body text-sm text-muted-foreground mt-0.5">
              {section?.description}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <p className={`font-body text-sm font-medium ${
              isComplete ? 'text-success' : 'text-muted-foreground'
            }`}>
              {photos?.length}/{section?.requiredPhotos} photos
            </p>
            {!isComplete && remainingPhotos > 0 && (
              <p className="font-caption text-xs text-warning">
                {remainingPhotos} more required
              </p>
            )}
          </div>
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={20}
            strokeWidth={2}
            className="text-muted-foreground"
          />
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 border-t border-border space-y-4">
          {/* Photo Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-smooth ${
              isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}>
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Camera" size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <p className="font-body text-sm text-foreground mb-2">
                  Drag photos here or
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef?.current?.click()}
                  iconName="Upload"
                >
                  Browse Files
                </Button>
              </div>
              <p className="font-caption text-xs text-muted-foreground">
                JPG, PNG, WebP (Max 5MB per file)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Photo Grid */}
          {photos?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {photos?.map((photo, index) => (
                <div key={photo?.id} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-muted border-2 border-border transition-smooth group-hover:border-primary">
                    <img
                      src={photo?.preview}
                      alt={`${section?.title} photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={() => handleRemove(photo?.id)}
                    className="absolute top-2 right-2 w-7 h-7 bg-error text-error-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth hover:scale-110"
                    aria-label="Remove photo"
                  >
                    <Icon name="X" size={14} strokeWidth={2.5} />
                  </button>
                  <div className="absolute bottom-2 left-2 right-2 bg-background/90 backdrop-blur-sm rounded px-2 py-1">
                    <p className="font-caption text-xs text-foreground truncate">
                      Photo {index + 1}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Notes Section */}
          <div>
            <label className="font-body text-sm font-medium text-foreground mb-2 block">
              Condition Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={handleNotesChange}
              placeholder={`Describe the condition of ${section?.title?.toLowerCase()}...`}
              maxLength={500}
              rows={3}
              className="w-full px-4 py-3 rounded-md border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            <p className="font-caption text-xs text-muted-foreground mt-1 text-right">
              {notes?.length}/500 characters
            </p>
          </div>

          {/* Completion Status */}
          {isComplete && (
            <div className="bg-success/10 border border-success/20 rounded-md p-3 flex items-center space-x-2">
              <Icon name="CheckCircle2" size={16} className="text-success" strokeWidth={2} />
              <p className="font-body text-sm text-success font-medium">
                Section complete! All required photos captured.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InspectionSection;